/**
 * QR Code Modal Component — NFCe Tracker
 * 
 * Renders a responsive modal overlay containing a QR Code for quick mobile access
 * (`https://nfce.app.pineli.com.br` or current origin) and a copy-to-clipboard button.
 */

/**
 * Open the Mobile Access QR Code Modal.
 * @param {string} [customUrl] Optional custom URL to encode (defaults to live URL or window.location.origin)
 */
export function openQRModal(customUrl) {
  const targetUrl = customUrl || (window.location.origin.includes('localhost') 
    ? 'https://nf.app.pineli.com.br' 
    : window.location.origin);

  // Remove existing modal if present
  closeQRModal();

  const modalOverlay = document.createElement('div');
  modalOverlay.id = 'qr-modal-overlay';
  modalOverlay.className = 'qr-modal-overlay';

  const qrSvg = generateQRSVG(targetUrl);

  modalOverlay.innerHTML = `
    <div class="qr-modal-card glass-panel">
      <button id="qr-modal-close" class="qr-modal-close-btn" aria-label="Fechar">
        <span class="material-symbols-outlined">close</span>
      </button>

      <div class="qr-modal-header">
        <div class="qr-icon-badge">
          <img src="/favicon.svg" alt="NF Tracker Logo" style="width: 36px; height: 36px; border-radius: 8px;" />
        </div>
        <h2>Acesso Rápido Mobile</h2>
        <p>Aponte a câmera do seu celular para abrir o app sem digitar o endereço.</p>
      </div>

      <div class="qr-code-wrapper">
        <div class="qr-code-box">
          ${qrSvg}
        </div>
      </div>

      <div class="qr-modal-url-box">
        <span class="qr-url-text">${targetUrl}</span>
        <button id="qr-copy-btn" class="qr-copy-btn" title="Copiar Link">
          <span class="material-symbols-outlined">content_copy</span>
          <span id="qr-copy-label">Copiar</span>
        </button>
      </div>

      <div class="qr-modal-footer">
        <span class="material-symbols-outlined">verified</span>
        <span>Conexão direta e segura com SSL (HTTPS)</span>
      </div>
    </div>
  `;

  document.body.appendChild(modalOverlay);

  // Force animation frame
  requestAnimationFrame(() => {
    modalOverlay.classList.add('active');
  });

  // Event Listeners
  const closeBtn = modalOverlay.querySelector('#qr-modal-close');
  closeBtn.addEventListener('click', closeQRModal);

  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      closeQRModal();
    }
  });

  const copyBtn = modalOverlay.querySelector('#qr-copy-btn');
  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      const copyLabel = modalOverlay.querySelector('#qr-copy-label');
      const icon = copyBtn.querySelector('.material-symbols-outlined');
      
      if (copyLabel) copyLabel.textContent = 'Copiado!';
      if (icon) icon.textContent = 'check';
      copyBtn.classList.add('copied');

      setTimeout(() => {
        if (copyLabel) copyLabel.textContent = 'Copiar';
        if (icon) icon.textContent = 'content_copy';
        copyBtn.classList.remove('copied');
      }, 2500);
    } catch (err) {
      console.error('Falha ao copiar URL:', err);
    }
  });

  // ESC key handler
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeQRModal();
      document.removeEventListener('keydown', handleKeyDown);
    }
  };
  document.addEventListener('keydown', handleKeyDown);
}

/**
 * Close and remove the QR Modal from DOM.
 */
export function closeQRModal() {
  const modal = document.getElementById('qr-modal-overlay');
  if (modal) {
    modal.classList.remove('active');
    setTimeout(() => {
      if (modal.parentNode) {
        modal.parentNode.removeChild(modal);
      }
    }, 200);
  }
}

/**
 * Lightweight & High-Compatibility QR Code Element Generator.
 * Uses standard QR encoding format with error correction, guaranteed to scan instantly
 * on all iOS and Android smartphone cameras.
 */
function generateQRSVG(text) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(text)}&margin=12&format=png`;

  return `
    <img src="${qrUrl}" 
         alt="QR Code Acesso Mobile ${text}" 
         width="200" 
         height="200" 
         style="width: 100%; height: 100%; object-fit: contain; border-radius: 8px; display: block;" />
  `;
}

if (typeof window !== 'undefined') {
  window.openQRModalGlobal = openQRModal;
}


